/**
 * Groups → authorities → what a control may do.
 *
 * This table is a COPY of the one the API GATEWAY is to enforce (see
 * docs/AUTHORITIES.md). Enforcement lives at the gateway by design — one check
 * covering every product's routes, so a role is added or changed in one place
 * rather than reimplemented per backend. The duplication here is deliberate:
 * the console has to decide what a control looks like before it calls
 * anything. But it means the two can disagree, in both directions:
 *
 *   - a user just added to a group holds a token minted before the change
 *   - this map is edited here and not at the gateway, or vice versa
 *
 * So: what is computed here is AFFORDANCE. A 403 is TRUTH — and today there is
 * no 403 to receive, because the gateway check has not shipped, which makes
 * this map the only gating there is. That is a reason to keep the write paths
 * honest, not to lean on them: every one must handle Forbidden even while
 * nothing returns it. See `Gate` (where the stop actually happens) and the
 * write hooks.
 */

export const AUTHORITIES = [
  "tenant:read",
  "tenant:write",
  "block:write",
  "quota:read",
  "quota:write",
  "quota:force",
  "settings:read",
  "settings:write",
  "service:restart",
  // Fleet-wide reads. Deliberately NOT folded into tenant:read — the MCP API
  // reference makes the same split and says why: the routes divide into
  // per-tenant reads and fleet-wide ones, and a support role that may look up
  // one merchant's record should not thereby see a fleet dashboard or every
  // admin action anyone has taken. Separate names so they can be revoked
  // separately later, even though every group holds both today.
  "audit:read",
  "fleet:read",
] as const;

export type Authority = (typeof AUTHORITIES)[number];

/** What each authority unlocks, in the operator's words. Shown on /me. */
export const AUTHORITY_MEANING: Record<Authority, string> = {
  "tenant:read": "See merchants, tiers, history and usage",
  "tenant:write": "Change a merchant's plan tier",
  "block:write": "Block or unblock a merchant or a user",
  "quota:read": "See tool caps and the catalog",
  "quota:write": "Change tool caps",
  "quota:force": "Override a safety guard on a cap change",
  "settings:read": "See configuration",
  "settings:write": "Change configuration",
  "service:restart": "Restart the live merchant-facing service",
  "audit:read": "See who changed what through this console",
  "fleet:read": "See fleet-wide totals across every merchant",
};

/**
 * The four groups from docs/AUTHORITIES.md. A test asserts these names match
 * the ones the Cognito pool actually creates — a typo like `admin-read-only`
 * deploys cleanly and then grants nobody anything, because a group no
 * authority map knows about is indistinguishable from no group at all.
 */
export const GROUP_AUTHORITIES: Record<string, readonly Authority[]> = {
  "admin-readonly": ["tenant:read", "quota:read", "settings:read", "audit:read", "fleet:read"],
  "admin-support": [
    "tenant:read",
    "quota:read",
    "settings:read",
    "audit:read",
    "fleet:read",
    "block:write",
  ],
  "admin-operator": [
    "tenant:read",
    "quota:read",
    "settings:read",
    "audit:read",
    "fleet:read",
    "block:write",
    "tenant:write",
    "quota:write",
    "settings:write",
  ],
  "admin-owner": [...AUTHORITIES],
};

export const KNOWN_GROUPS = Object.keys(GROUP_AUTHORITIES);

export interface ResolvedAccess {
  /** Groups from the token, verbatim. */
  groups: string[];
  /** The union of every known group's authorities. */
  authorities: Set<Authority>;
  /**
   * Groups in the token that this map does not know.
   *
   * Surfaced on /me rather than ignored: a group that grants nothing looks
   * identical to having no group, and the difference is a deploy bug someone
   * needs to fix.
   */
  unknownGroups: string[];
}

/** Multiple groups are the UNION of their authorities — never a deny rule. */
export function resolveAccess(groups: string[] | undefined): ResolvedAccess {
  const list = groups ?? [];
  const authorities = new Set<Authority>();
  const unknownGroups: string[] = [];

  for (const g of list) {
    const granted = GROUP_AUTHORITIES[g];
    if (!granted) {
      unknownGroups.push(g);
      continue;
    }
    for (const a of granted) authorities.add(a);
  }

  return { groups: list, authorities, unknownGroups };
}

/** Which groups would grant this authority — the actionable half of a refusal. */
export function grantedBy(authority: Authority): string[] {
  return KNOWN_GROUPS.filter((g) => GROUP_AUTHORITIES[g]!.includes(authority));
}

/**
 * Why a control is inert, phrased so the reader can act on it.
 *
 * Names the missing authority AND the group that grants it: "needs
 * quota:force" is unactionable if you do not know what to ask for.
 */
export function reasonFor(authority: Authority, access: ResolvedAccess): string {
  const groups = grantedBy(authority);
  const have = access.groups.length ? access.groups.join(", ") : "no group";
  return `Needs ${authority} — granted by ${groups.join(" or ")}. You have ${have}.`;
}
